// Main Application JavaScript
// This file imports all JavaScript modules and compiles them into a single application.js bundle

// ============================================================================
// Core Utilities
// ============================================================================
import csrf from './utils/csrf.js';
import api from './utils/api.js';
import dom from './utils/dom.js';
import './utils/crud-actions.js';    // Auto-initializes (data-delete-url, data-bulk-url, data-duplicate-url)

// ============================================================================
// Core Modules
// ============================================================================
import notifications from './modules/notifications.js';
import './modules/bulk-actions.js';  // Auto-initializes
import './modules/filters.js';        // Auto-initializes
import './modules/ajax-delete.js';    // Auto-initializes
import './modules/sidebar.js';        // Auto-initializes (sidebar submenu toggles)

// ============================================================================
// Shared Component Modules
// ============================================================================
import './modules/tabs.js';              // Auto-initializes (tab switching)
import './modules/data-table.js';        // Auto-initializes (table checkboxes, delete)
import './modules/pagination.js';        // Auto-initializes (per-page selector)
import './modules/color-swatches.js';    // Auto-initializes (swatch selection)
import './modules/restaurant-switcher.js'; // Auto-initializes (dev switcher tool)

// ============================================================================
// Feature Modules
// ============================================================================
// Import modifiers functionality
import './modifiers.js';

// ============================================================================
// CRUD Modules
// ============================================================================
import './modules/products.js';          // Auto-initializes (product CRUD)
import './modules/categories.js';        // Auto-initializes (category CRUD)
import './modules/customers.js';         // Customer filters
import './modules/orders.js';            // Order filters and sorting
import './modules/coupons.js';           // Coupon filters
import './modules/users.js';             // User filters (role, status)
import './modules/inventory.js';         // Inventory modal and filters
import './modules/reservations-actions.js'; // Reservation actions (confirm, seat, cancel)
import './modules/seating.js';           // Seating occupancy metrics
import './modules/settings.js';          // Settings tab manager
import './modules/spaces.js';            // Spaces and rooms (filters, keyboard shortcuts)
import './modules/analytics.js';         // Chart.js wrappers for analytics pages
import './modules/dashboard.js';         // Dashboard floor plan table management
import './modules/giftcards.js';         // Gift card forms and templates
import './modules/loyalty.js';           // Loyalty program forms
import './modules/promotions.js';        // Promotion forms and validation
import './modules/storefront.js';        // Storefront reservations, gift cards
import './modules/media.js';             // Media library upload and management
import './modules/misc.js';              // Miscellaneous utilities
import './modules/admin.js';             // Admin restaurant and plan management
import './modules/orders-form.js';       // Order creation/editing
import './modules/restaurants.js';       // Restaurant management

// Log when loaded (useful for debugging)
console.log('Application JavaScript loaded');

// ============================================================================
// Expose utilities globally for legacy code compatibility
// ============================================================================
window.App = {
    csrf,
    api,
    dom,
    notifications
};

// ============================================================================
// Global Form Helpers with CSRF Protection
// ============================================================================

/**
 * Submits a DELETE request via form submission with CSRF protection.
 * Uses the global window.csrfToken set in the layout.
 *
 * @param {string} url - The URL to submit to (e.g., '/products/123')
 * @param {string} [confirmMessage] - Optional confirmation message. If provided, shows confirm dialog.
 * @returns {boolean} - Returns false if user cancelled, true if form was submitted
 *
 * @example
 * // Simple delete
 * submitDelete('/products/123');
 *
 * // Delete with confirmation
 * submitDelete('/products/123', 'Are you sure you want to delete this item?');
 */
window.submitDelete = function(url, confirmMessage) {
    if (confirmMessage && !confirm(confirmMessage)) {
        return false;
    }

    var form = document.createElement('form');
    form.method = 'POST';
    form.action = url;

    var methodInput = document.createElement('input');
    methodInput.type = 'hidden';
    methodInput.name = '_method';
    methodInput.value = 'DELETE';
    form.appendChild(methodInput);

    var tokenInput = document.createElement('input');
    tokenInput.type = 'hidden';
    tokenInput.name = '_token';
    tokenInput.value = window.csrfToken;
    form.appendChild(tokenInput);

    document.body.appendChild(form);
    form.submit();
    return true;
};

/**
 * Submits a form request with a custom HTTP method (PUT, PATCH, DELETE) and CSRF protection.
 * Uses the global window.csrfToken set in the layout.
 *
 * @param {string} method - The HTTP method (PUT, PATCH, DELETE)
 * @param {string} url - The URL to submit to
 * @param {Object} [data] - Optional additional form data as key-value pairs
 * @param {string} [confirmMessage] - Optional confirmation message
 * @returns {boolean} - Returns false if user cancelled, true if form was submitted
 *
 * @example
 * // PUT request
 * submitWithMethod('PUT', '/products/123', {status: 'active'});
 *
 * // DELETE with extra data and confirmation
 * submitWithMethod('DELETE', '/orders/456', {reason: 'cancelled'}, 'Cancel this order?');
 */
window.submitWithMethod = function(method, url, data, confirmMessage) {
    if (confirmMessage && !confirm(confirmMessage)) {
        return false;
    }

    var form = document.createElement('form');
    form.method = 'POST';
    form.action = url;

    var methodInput = document.createElement('input');
    methodInput.type = 'hidden';
    methodInput.name = '_method';
    methodInput.value = method.toUpperCase();
    form.appendChild(methodInput);

    var tokenInput = document.createElement('input');
    tokenInput.type = 'hidden';
    tokenInput.name = '_token';
    tokenInput.value = window.csrfToken;
    form.appendChild(tokenInput);

    // Add any additional data
    if (data && typeof data === 'object') {
        for (var key in data) {
            if (data.hasOwnProperty(key)) {
                var input = document.createElement('input');
                input.type = 'hidden';
                input.name = key;
                input.value = data[key];
                form.appendChild(input);
            }
        }
    }

    document.body.appendChild(form);
    form.submit();
    return true;
};

// Legacy jQuery restaurant switcher removed — handled by modules/restaurant-switcher.js

// NOTE: Sidebar submenu toggles, mobile toggle, and restaurant selector dropdown
// are now handled by modules/sidebar.js

// ============================================================================
// User Form Validation
// ============================================================================

/**
 * Initialize user form validation (for both new and edit user forms)
 */
function initUserFormValidation() {
    const userForm = document.getElementById('user-form');
    if (!userForm) return;

    const setPasswordCheckbox = document.getElementById('set_password');
    const passwordInput = document.getElementById('password');
    const passwordConfirmationInput = document.getElementById('password_confirmation');

    const syncPasswordInputs = () => {
        const enablePassword = Boolean(setPasswordCheckbox?.checked);

        [passwordInput, passwordConfirmationInput].forEach((input) => {
            if (!input) return;

            input.disabled = !enablePassword;
            if (!enablePassword) {
                input.value = '';
                input.setCustomValidity('');
                input.classList.remove('border-red-500');
            }
        });
    };

    if (setPasswordCheckbox) {
        syncPasswordInputs();
        setPasswordCheckbox.addEventListener('change', syncPasswordInputs);
    }

    // Form submission validation
    userForm.addEventListener('submit', function(e) {
        const firstName = document.getElementById('first_name')?.value.trim();
        const lastName = document.getElementById('last_name')?.value.trim();
        const username = document.getElementById('username')?.value.trim();
        const email = document.getElementById('email')?.value.trim();
        const password = passwordInput?.value;
        const passwordConfirmation = passwordConfirmationInput?.value;
        const pin = document.getElementById('pin')?.value.trim();

        // Name validation
        if (!firstName || !lastName) {
            alert('Please enter both first and last name');
            e.preventDefault();
            return;
        }

        // Username validation
        if (!username || username.length < 3) {
            alert('Username must be at least 3 characters long');
            e.preventDefault();
            return;
        }

        // Email validation (optional for PIN-only users)
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (email && !emailRegex.test(email)) {
            alert('Please enter a valid email address');
            e.preventDefault();
            return;
        }

        // Password validation (optional for PIN-only users)
        const passwordEnabled = !passwordInput?.disabled;
        const hasPasswordInput = passwordEnabled && password !== undefined;
        const wantsPassword = hasPasswordInput && ((password && password.length > 0) || (passwordConfirmation && passwordConfirmation.length > 0));
        if (wantsPassword) {
            if (!password || password.length < 8) {
                alert('Password must be at least 8 characters long');
                e.preventDefault();
                return;
            }

            // Password confirmation validation
            if (password !== passwordConfirmation) {
                alert('Passwords do not match');
                e.preventDefault();
                return;
            }
        }

        // PIN validation (only if provided)
        if (pin && !/^\d{4,6}$/.test(pin)) {
            alert('PIN must be 4-6 digits');
            e.preventDefault();
            return;
        }
    });

    // Real-time password match validation
    if (passwordConfirmationInput) {
        passwordConfirmationInput.addEventListener('input', function() {
            if (passwordConfirmationInput.disabled) {
                this.setCustomValidity('');
                this.classList.remove('border-red-500');
                return;
            }

            const password = passwordInput?.value ?? '';
            const confirmation = this.value;

            if (confirmation && password !== confirmation) {
                this.setCustomValidity('Passwords do not match');
                this.classList.add('border-red-500');
            } else {
                this.setCustomValidity('');
                this.classList.remove('border-red-500');
            }
        });
    }

    // Real-time PIN validation
    const pinInput = document.getElementById('pin');
    if (pinInput) {
        pinInput.addEventListener('input', function() {
            const pin = this.value;

            // Remove non-numeric characters
            this.value = pin.replace(/\D/g, '');

            if (this.value && (this.value.length < 4 || this.value.length > 6)) {
                this.setCustomValidity('PIN must be 4-6 digits');
                this.classList.add('border-red-500');
            } else {
                this.setCustomValidity('');
                this.classList.remove('border-red-500');
            }
        });
    }
}

// Initialize user form validation when DOM is ready
document.addEventListener('DOMContentLoaded', initUserFormValidation);
